import { FormikHelpers, FormikValues } from 'formik';
import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { object, string } from 'yup';

import Botao from 'Component/Botao';
import BotaoLink from 'Component/BotaoLink';
import Buscando from 'Component/Buscando';
import Cabecalho from 'Component/Cabecalho';
import { CurriculoLista } from 'Component/Curriculo';
import Form, { Campo, Selecao } from 'Component/Form';
import LayoutDefault from 'Component/Layout';
import Paginacao from 'Component/Paginacao';
import SemRegistros from 'Component/SemRegistros';
import { useRecuperarCategoriasDeCurriculoQuery } from 'View/Service/Default/CategoriasDeCurriculo';
import { useRecuperarCurriculosQuery } from 'View/Service/Default/Curriculos';
import { useRecuperarDepartamentosQuery } from 'View/Service/Default/Departamentos';

const regras = object({
  busca: string().optional().min(3, 'Deve ter mais que 3 letras'),
});

const Default = (): React.ReactElement => {
  const [searchParams, setSearchParams] = useSearchParams();

  const { data: { departamentos } = {} } = useRecuperarDepartamentosQuery({
    finder: 'listaJson',
    limit: 200,
    sort: 'nome',
  });

  const { data: { categorias } = {} } = useRecuperarCategoriasDeCurriculoQuery({
    finder: 'listaJson',
    limit: 200,
    sort: 'nome',
  });

  const {
    data: { curriculos, paginacao } = {},
    refetch,
    isFetching,
    isLoading,
  } = useRecuperarCurriculosQuery({
    contain: ['Departamentos', 'CategoriasDeCurriculo'],
    page: searchParams.get('pagina') || 1,
    options: {
      busca: { termo: searchParams.get('busca') || '' },
      categoria_id: searchParams.get('categoria_id') || '',
      departamento_id: searchParams.get('departamento_id') || '',
    },
  });

  const filtrar = (
    dados: { busca: string; categoria_id: string; departamento_id: string },
    form: FormikHelpers<FormikValues>
  ) => {
    if (dados.departamento_id) {
      searchParams.set('departamento_id', dados.departamento_id);
    } else {
      searchParams.delete('departamento_id');
    }
    if (dados.categoria_id) {
      searchParams.set('categoria_id', dados.categoria_id);
    } else {
      searchParams.delete('categoria_id');
    }
    searchParams.set('busca', dados.busca);
    searchParams.set('pagina', '1');

    setSearchParams(searchParams);

    form.setSubmitting(false);
  };

  return (
    <LayoutDefault>
      <Cabecalho
        titulo="Currículos"
        descricao="Listagem de currículos cadastradas no banco de dados"
      />
      <Form
        emLinha
        onSubmit={filtrar}
        regras={regras}
        desabilitado={isFetching}
      >
        <div className="row">
          <div className="col-md-6 mb-3">
            <Botao icone="arrow-clockwise" onClick={refetch} className="me-2" />
            <BotaoLink
              tema="success"
              icone="plus-lg"
              to="/curriculos/adicionar"
            >
              Adicionar
            </BotaoLink>
          </div>
          <div className="col-md-6 mb-3">
            <div className="hstack gap-3">
              <Selecao
                name="departamento_id"
                opcoes={departamentos}
                padrao="Departamento"
              />
              <Selecao
                name="categoria_id"
                opcoes={categorias}
                padrao="Categoria"
              />
              <Campo name="busca" rotulo="Digite o que deseja buscar" />
              <Botao type="submit" icone="filter" />
            </div>
          </div>
        </div>
      </Form>
      <Buscando exibir={isLoading} />
      <SemRegistros
        carregando={isLoading}
        registros={curriculos?.length}
        redirecionar="/curriculos/adicionar"
      />
      <div className="bg-white shadow-sm rounded">
        {curriculos?.map((curriculo) => (
          <CurriculoLista
            key={`${curriculo.id}-${curriculo.modificado}`}
            dados={curriculo}
          />
        ))}
      </div>
      <Paginacao dados={paginacao} desabilitado={isFetching} />
    </LayoutDefault>
  );
};

export default Default;
